/**
 * @file index.tsx
 *
 * @fileoverview Renders the entire filters section.
 */
import React, { useContext } from 'react';
import { processFacets } from '~/jobiqo/components/Search/utils/searchHelpers';
import FacetFilters from '~/jobiqo/components/Search/facets/facet-filters';
import { JobSearchForm } from '~/components/Form/SearchInputForm';
import { Facet, FacetConfig } from '~/jobiqo/models/search';
import { ParsedUrlQuery } from 'querystring';
import { HideMobile } from '~/vcl/atoms/06-layout/02-visibility/02-hide-mobile';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { LayoutContext, LayoutContextInterface } from '~/jobiqo/context/layout';
import {
  SortingFilter,
  SortingFilterTab,
  SortingFilterTabPanel,
} from '~/jobiqo/components/JobSearch/components/SortingFilter';

export interface FiltersSectionProps {
  /**
   * Gets which filters are currently selected.
   */
  getFilterSelectedItems: (querystring: any, facet: Facet) => string[];
  /**
   * Callback when a filter is toggled (added / removed).
   */
  toggleFilter: (filter: string, selectedItems: string[]) => void;
  /**
   * The current querystring object.
   */
  querystring: ParsedUrlQuery;
  /**
   * The current pathname.
   */
  pathname: string;
  /**
   * The list of facets / filters being used.
   */
  facets: Facet[];
  /**
   * If the search form should be hidden on mobile (when viewing job detail).
   */
  hiddenForm: boolean;
  /**
   * The facets configurations.
   */
  jobSearchFacetsConfig: FacetConfig[];
  /**
   * Search URL to use for redirects.
   */
  searchUrl?: string;
  /**
   * Render only the keyword search.
   */
  fullTextOnly?: boolean;
  /**
   * The radius search flag.
   */
  radiusSearch: boolean;
}

export const FiltersSection = ({
  getFilterSelectedItems,
  querystring,
  pathname,
  toggleFilter,
  facets,
  hiddenForm,
  jobSearchFacetsConfig,
  searchUrl,
  fullTextOnly = false,
  radiusSearch = true,
}: FiltersSectionProps) => {
  const { isMobile }: LayoutContextInterface = useContext(LayoutContext);

  const content = (
    <>
      <Spacing size="medium" />
      <JobSearchForm
        querystring={querystring}
        searchUrl={searchUrl}
        hideGeoSearch={fullTextOnly}
        radiusSearch={radiusSearch}
      />
      <Spacing />
      {!fullTextOnly && (
        <FacetFilters
          getFacetSelectedItems={(facet) =>
            getFilterSelectedItems(querystring, facet)
          }
          onFacetToggle={toggleFilter}
          facets={processFacets(facets, jobSearchFacetsConfig)}
          querystring={querystring}
          pathname={pathname}
          renderAdditionalFilters={() => (
            <SortingFilter
              toggleFilter={toggleFilter}
              querystring={querystring}
            />
          )}
          renderAdditionalTabs={() => <SortingFilterTab />}
          renderAdditionalTabPanels={() => (
            <SortingFilterTabPanel
              querystring={querystring}
              toggleFilter={toggleFilter}
            />
          )}
        />
      )}
      <Spacing size="medium" />
    </>
  );

  // Keep the desktop tree stable to avoid remounting facet popovers when a
  // job gets selected. On mobile we still hide the search form in detail view.
  if (hiddenForm && isMobile) {
    return <HideMobile>{content}</HideMobile>;
  }

  return content;
};
export default FiltersSection;
